/**
 * One ad inside an ad group card: name, format, identity, creative (upload or
 * reuse an existing library video), copy, CTA, landing URL — plus the
 * transcoding gate and the AI copy enhancer. Focusing any field binds the
 * right-hand preview to this ad.
 */
import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  CheckCircle2, Copy, ExternalLink, FileUp, Film, FolderOpen, Loader2, Lock, RefreshCw, Trash2, Upload, UserPlus, Wand2, X,
} from 'lucide-react';
import { useTiktokWorkspace, type WorkspaceAd, type WorkspaceAdGroup } from '@/contexts/TiktokWorkspaceContext';
import { tiktokService } from '@/services/tiktok.service';
import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import type { ModelPickerValue } from '@/components/ModelPicker';
import { TiktokModelPicker } from './TiktokModelPicker';
import { adDestinationFor, defaultGoalFor } from '@/lib/tiktok-matrix';
import { tiktokInstantFormsUrl } from '@/lib/tiktokLinks';
import { useTiktokAccountCurrency } from '@/hooks/useTiktokAccountCurrency';
import { adIssues } from './validation';
import { videoSpecProblem } from './saveHelpers';
import { ExistingCreativePicker, type PickedFromAd, type PickedImage, type PickedVideo } from './ExistingCreativePicker';
import {
  AD_TEXT_MAX, CTA_OPTIONS, Pill, adForm, groupForm, groupPlacementsOf, identityFitsPlacement, inputCls,
  type Identity, type VideoGate,
} from './workspaceShared';

type AdFormat = 'SINGLE_VIDEO' | 'SINGLE_IMAGE' | 'CAROUSEL_ADS' | 'COLLECTION_ADS';

export interface AdCardProps {
  adGroup: WorkspaceAdGroup;
  ad: WorkspaceAd;
  index: number;
  objective?: string;
  identities: Identity[];
  identitiesLoading: boolean;
  reloadIdentities: () => Promise<unknown>;
  leadForms: Array<{ pageId: string; title: string }> | null;
  leadFormsLoading: boolean;
  reloadLeadForms: () => Promise<unknown>;
  focused: boolean;
  onFocus: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
}

export const AdCard: React.FC<AdCardProps> = ({
  adGroup, ad, index, objective, identities, identitiesLoading, reloadIdentities,
  leadForms, leadFormsLoading, reloadLeadForms, focused, onFocus, onDelete, onDuplicate,
}) => {
  const { updateAdForm } = useTiktokWorkspace();
  const { account: tiktokAccount } = useTiktokAccountCurrency();

  const [uploading, setUploading] = useState(false);
  const [mediaTab, setMediaTab] = useState<'upload' | 'existing'>('upload');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [identityModalOpen, setIdentityModalOpen] = useState(false);
  const [newIdentityName, setNewIdentityName] = useState('');
  const [creatingIdentity, setCreatingIdentity] = useState(false);
  const [aiModel, setAiModel] = useState<ModelPickerValue | undefined>(undefined);
  const [enhancing, setEnhancing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const f = adForm(ad);
  const formRef = useRef(f);
  formRef.current = f;
  const g = groupForm(adGroup);
  const locked = ad.isLocked;

  const set = (key: string, val: unknown) =>
    updateAdForm(adGroup.id, ad.id, { [key]: val } as Record<string, unknown>);
  const setMany = (patch: Record<string, unknown>) => updateAdForm(adGroup.id, ad.id, patch);

  // TikTok's in-feed placement only serves video (and carousel) — image-only
  // formats are Pangle / Global App Bundle ("The source of this post is invalid").
  const tiktokPlacement = groupPlacementsOf(adGroup).includes('PLACEMENT_TIKTOK');
  const adFormat = ((f.adFormat as string) ?? 'SINGLE_VIDEO') as AdFormat;
  const adName = (f.adName as string) ?? '';
  const adText = (f.adText as string) ?? '';
  const callToAction = (f.callToAction as string) ?? 'LEARN_MORE';
  const landingPageUrl = (f.landingPageUrl as string) ?? '';
  const creativeUrl = (f.creativeUrl as string) ?? '';
  const resolvedImageId = (f.resolvedImageId as string) ?? '';
  const resolvedVideoId = (f.resolvedVideoId as string) ?? '';
  const carouselImageIds: string[] = Array.isArray(f.carouselImageIds) ? (f.carouselImageIds as string[]) : [];
  const creativeFileName = (f.creativeFileName as string) ?? '';
  const pageId = (f.pageId as string) ?? '';
  const videoGate = ((f.videoGate as VideoGate | undefined) ?? { state: 'idle' }) as VideoGate;

  // LEAD_GENERATION ads open an Instant Form (page_id) instead of a URL,
  // FOLLOWERS ads carry no destination at all, app ads only a CTA.
  const groupGoal = (g.optimizationGoal as string) || defaultGoalFor(objective);
  const destination = adDestinationFor(objective, groupGoal);
  const needsLeadForm = destination === 'form';
  const showCta = destination !== 'forbidden';
  const showLanding = destination === 'required';

  const compatibleIdentities = identities.filter((i) => identityFitsPlacement(i.identityType, tiktokPlacement));
  const identityId = (f.identityId as string) ?? '';
  const selectedIdentity = identities.find((i) => i.identityId === identityId);
  const selectedIdentityFits = !identityId || identityFitsPlacement(selectedIdentity?.identityType ?? '', tiktokPlacement);

  // Default to an identity that actually works on this placement, and persist
  // it so validation / publish see the same value the select shows.
  useEffect(() => {
    if (locked || identityId || compatibleIdentities.length === 0) return;
    set('identityId', compatibleIdentities[0].identityId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked, identityId, compatibleIdentities.length, tiktokPlacement]);

  // ── Video readiness gate ──────────────────────────────────────────────────
  // TikTok transcodes uploads asynchronously; an ad saved against a video
  // that's still processing fails on its placeholder cover, and a video below
  // TikTok's minimum specs NEVER finishes. Poll until PROCESS_OK and persist
  // the outcome on the ad so the review step and pills can read it.
  useEffect(() => {
    if (adFormat !== 'SINGLE_VIDEO' || !resolvedVideoId || locked) {
      if (formRef.current.videoGate && (formRef.current.videoGate as VideoGate).state !== 'idle' && !locked) set('videoGate', { state: 'idle' });
      return;
    }
    let cancelled = false;
    set('videoGate', { state: 'processing' });
    const poll = async () => {
      for (let i = 0; i < 60 && !cancelled; i++) {
        try {
          const s = await tiktokService.videoStatus(resolvedVideoId);
          if (cancelled) return;
          const patch: Record<string, unknown> = {};
          if (s.coverUrl && !formRef.current.previewCoverUrl) patch.previewCoverUrl = s.coverUrl;
          if (s.videoUrl && !formRef.current.previewVideoUrl) patch.previewVideoUrl = s.videoUrl;
          const problem = videoSpecProblem(s);
          if (problem) { setMany({ ...patch, videoGate: { state: 'rejected', reason: problem } }); return; }
          if (s.status === 'PROCESS_OK') { setMany({ ...patch, videoGate: { state: 'ready', width: s.width, height: s.height } }); return; }
          setMany({ ...patch, videoGate: { state: 'processing', width: s.width, height: s.height } });
        } catch { /* transient — keep polling */ }
        await new Promise((r) => setTimeout(r, 4000));
      }
      if (!cancelled) set('videoGate', { state: 'rejected', reason: 'still processing after 4 minutes — the file may not meet TikTok\'s specs. Try re-exporting it.' });
    };
    void poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adFormat, resolvedVideoId, locked]);

  const clearMedia = () => setMany({
    resolvedImageId: '', resolvedVideoId: '', carouselImageIds: [], creativeUrl: '',
    previewCoverUrl: '', previewVideoUrl: '', creativeFileName: '', videoGate: { state: 'idle' },
  });

  const uploadCreative = async () => {
    const url = creativeUrl.trim();
    if (!url) { toast.error('Paste an image / video URL first'); return; }
    setUploading(true);
    try {
      if (adFormat === 'SINGLE_IMAGE') {
        const r = await tiktokService.uploadImageByUrl({ imageUrl: url });
        setMany({ resolvedImageId: r.imageId, resolvedVideoId: '', previewCoverUrl: r.imageUrl, previewVideoUrl: '', creativeFileName: r.fileName ?? url.split('/').pop() ?? '' });
        toast.success(`Image uploaded · id ${r.imageId}`);
      } else if (adFormat === 'SINGLE_VIDEO') {
        const r = await tiktokService.uploadVideoByUrl({ videoUrl: url });
        setMany({ resolvedVideoId: r.videoId, resolvedImageId: '', previewCoverUrl: '', previewVideoUrl: '', creativeFileName: url.split('/').pop() ?? '' });
        toast.success(`Video uploaded · id ${r.videoId}`);
      } else if (adFormat === 'CAROUSEL_ADS') {
        const r = await tiktokService.uploadImageByUrl({ imageUrl: url });
        const next = [...carouselImageIds, r.imageId];
        setMany({ carouselImageIds: next, creativeUrl: '', previewCoverUrl: formRef.current.previewCoverUrl || r.imageUrl });
        toast.success(`Carousel image #${next.length} added`);
      } else {
        toast.error('Collection ads need a catalog — not supported yet.');
      }
    } catch {
      // axios interceptor toasts.
    } finally {
      setUploading(false);
    }
  };

  const uploadFile = async (file: File) => {
    setUploading(true);
    try {
      if (adFormat === 'SINGLE_VIDEO') {
        const r = await tiktokService.uploadVideoFile(file);
        setMany({ resolvedVideoId: r.videoId, resolvedImageId: '', previewCoverUrl: '', previewVideoUrl: '', creativeFileName: file.name });
        toast.success(`Video uploaded · id ${r.videoId}`);
      } else if (adFormat === 'CAROUSEL_ADS') {
        const r = await tiktokService.uploadImageFile(file);
        const next = [...carouselImageIds, r.imageId];
        setMany({ carouselImageIds: next, previewCoverUrl: formRef.current.previewCoverUrl || r.imageUrl });
        toast.success(`Carousel image #${next.length} added`);
      } else {
        const r = await tiktokService.uploadImageFile(file);
        setMany({ resolvedImageId: r.imageId, resolvedVideoId: '', previewCoverUrl: r.imageUrl, previewVideoUrl: '', creativeFileName: file.name });
        toast.success(`Image uploaded · id ${r.imageId}`);
      }
    } catch {
      // axios interceptor toasts.
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Reuse from the library — the backend derives (and caches) the cover image
  // from the video's own frame at ad create, exactly as for fresh uploads.
  const pickVideo = (v: PickedVideo) => {
    setMany({
      resolvedVideoId: v.videoId, resolvedImageId: '', creativeUrl: '',
      previewCoverUrl: v.coverUrl ?? '', previewVideoUrl: v.previewUrl ?? '', creativeFileName: v.fileName ?? v.videoId,
    });
    toast.success(v.displayable === false ? 'Video selected — still transcoding on TikTok' : 'Existing video selected');
  };
  const pickImage = (im: PickedImage) => {
    if (adFormat === 'CAROUSEL_ADS') {
      const next = [...carouselImageIds, im.imageId];
      setMany({ carouselImageIds: next, previewCoverUrl: formRef.current.previewCoverUrl || im.url || '' });
      toast.success(`Carousel image #${next.length} added`);
    } else {
      setMany({ resolvedImageId: im.imageId, resolvedVideoId: '', creativeUrl: '', previewCoverUrl: im.url ?? '', previewVideoUrl: '', creativeFileName: im.fileName ?? im.imageId });
      toast.success('Existing image selected');
    }
  };
  const pickFromAd = (ref: PickedFromAd) => {
    const patch: Record<string, unknown> = { creativeUrl: '', previewCoverUrl: '', previewVideoUrl: '' };
    if (ref.videoId) {
      patch.adFormat = 'SINGLE_VIDEO'; patch.resolvedVideoId = ref.videoId; patch.resolvedImageId = ''; patch.carouselImageIds = [];
      patch.creativeFileName = `from ad "${ref.adName}"`;
    } else if (ref.imageIds?.length) {
      if (ref.imageIds.length > 1) { patch.adFormat = 'CAROUSEL_ADS'; patch.carouselImageIds = ref.imageIds; patch.resolvedImageId = ''; }
      else { patch.adFormat = 'SINGLE_IMAGE'; patch.resolvedImageId = ref.imageIds[0]; patch.carouselImageIds = []; }
      patch.resolvedVideoId = '';
      patch.creativeFileName = `from ad "${ref.adName}"`;
    }
    // Copy fills only what's still empty — never overwrite what the user typed.
    if (!adText.trim() && ref.adText) patch.adText = ref.adText.slice(0, AD_TEXT_MAX);
    if (!landingPageUrl.trim() && ref.landingPageUrl) patch.landingPageUrl = ref.landingPageUrl;
    if (ref.callToAction && CTA_OPTIONS.some((c) => c.value === ref.callToAction)) patch.callToAction = ref.callToAction;
    setMany(patch);
    toast.success(`Creative from "${ref.adName}" applied`);
  };

  const enhance = async () => {
    setEnhancing(true);
    try {
      const r = await tiktokService.enhanceAdCopy({
        adFormat,
        objective: objective || undefined,
        draftAdName: adName || undefined,
        draftAdText: adText || undefined,
        draftCallToAction: callToAction || undefined,
        brandName: selectedIdentity?.displayName,
        landingPageUrl: landingPageUrl || undefined,
        provider: aiModel?.provider,
        model: aiModel?.model,
      });
      const patch: Record<string, unknown> = {};
      if (r.adName) patch.adName = r.adName;
      if (r.adText) patch.adText = r.adText.slice(0, AD_TEXT_MAX);
      if (r.callToAction) patch.callToAction = r.callToAction;
      setMany(patch);
      toast.success('Enhanced with AI');
    } catch {
      // axios interceptor toasts.
    } finally { setEnhancing(false); }
  };

  const createIdentity = async () => {
    if (!newIdentityName.trim()) { toast.error('Display name is required'); return; }
    setCreatingIdentity(true);
    try {
      const r = await tiktokService.createIdentity({ displayName: newIdentityName.trim() });
      toast.success(`Identity created · ${r.identityId}`);
      await reloadIdentities();
      set('identityId', r.identityId);
      setNewIdentityName('');
      setIdentityModalOpen(false);
    } catch {
      // axios interceptor toasts.
    } finally { setCreatingIdentity(false); }
  };

  const missing = adIssues(ad, adGroup, objective, identities);
  const isVideo = adFormat === 'SINGLE_VIDEO';
  const hasMedia = isVideo ? !!resolvedVideoId : adFormat === 'CAROUSEL_ADS' ? carouselImageIds.length > 0 : !!resolvedImageId;
  const mediaNote = hasMedia
    ? `${isVideo ? 'Video' : 'Image'} ${videoGate.state === 'processing' ? 'processing' : 'ready'} · ${creativeFileName || resolvedVideoId || resolvedImageId}`
    : null;

  return (
    <div
      onFocusCapture={onFocus}
      className={clsx(
        'rounded-xl border p-3.5 transition-colors',
        focused ? 'border-primary/40 bg-primary-soft/20' : 'border-border',
      )}
    >
      <div className="mb-3 flex items-center gap-2">
        <Film size={14} className="shrink-0 text-text-muted" />
        <span className="min-w-0 flex-1 truncate text-xs font-semibold text-text-primary">
          {adName || `Ad ${index + 1}`}
        </span>
        {locked ? <Pill tone="ok"><Lock size={9} /> Saved on TikTok</Pill>
          : missing.length === 0 ? <Pill tone="ok">ready</Pill>
          : <Pill tone="warn">{missing.length} missing</Pill>}
        <button type="button" onClick={onFocus} className="h-6 rounded-md px-2 text-[11px] font-medium text-text-secondary hover:bg-surface-alt hover:text-text-primary">
          Preview
        </button>
        <button type="button" onClick={onDuplicate} title="Duplicate ad" className="flex h-6 w-6 items-center justify-center rounded-md text-text-muted hover:bg-surface-alt hover:text-text-primary">
          <Copy size={12} />
        </button>
        {!locked && (
          <button type="button" onClick={onDelete} title="Remove ad" className="flex h-6 w-6 items-center justify-center rounded-md text-text-muted hover:bg-danger-soft hover:text-danger">
            <Trash2 size={13} />
          </button>
        )}
      </div>

      {!locked && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-primary/30 bg-primary-soft/40 p-2.5">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary text-on-primary">
            <Wand2 size={13} />
          </div>
          <div className="min-w-0 flex-1 text-2xs text-text-muted">
            <span className="font-semibold text-text-primary">Enhance with AI</span> — type rough drafts below, pick a model, click Enhance.
          </div>
          <TiktokModelPicker value={aiModel} onChange={setAiModel} />
          <Button variant="primary" size="sm" onClick={enhance} loading={enhancing} iconLeft={<Wand2 size={12} />}>Enhance</Button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
        <FormField label="Ad name (internal)" required>
          <input className={inputCls} aria-label="Ad name" placeholder="e.g. Eid · Hero · v1" value={adName} onChange={(e) => set('adName', e.target.value)} disabled={locked} />
        </FormField>

        <FormField label="Format" required hintBelow hint="TikTok rejects image and carousel creatives on this advertiser's placements — video is the supported format.">
          <select
            className={inputCls}
            aria-label="Ad format"
            value={adFormat}
            onChange={(e) => { set('adFormat', e.target.value); clearMedia(); }}
            disabled={locked}
          >
            {/* Live-verified: this advertiser's placements reject both image and
                carousel creatives ("The source of this post is invalid"). */}
            <option value="SINGLE_IMAGE" disabled>Single image — not accepted by TikTok here</option>
            <option value="SINGLE_VIDEO">Single video</option>
            <option value="CAROUSEL_ADS" disabled>Carousel — not accepted by TikTok here</option>
          </select>
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="TikTok identity"
            required
            hintBelow
            hint={tiktokPlacement
              ? 'The ad runs under this account\'s name and avatar. In-feed ads need an authorised TikTok account — custom identities are rejected there.'
              : 'Pangle / Global App Bundle placements only accept a custom identity (display name + image), not a TikTok account.'}
          >
            <div className="flex gap-2">
              <select
                className={clsx(inputCls, 'flex-1')}
                aria-label="TikTok identity"
                value={identityId}
                onChange={(e) => set('identityId', e.target.value)}
                disabled={locked || identities.length === 0}
              >
                {identities.length === 0 ? (
                  <option value="">{identitiesLoading ? 'Loading identities…' : '— No identities found —'}</option>
                ) : (
                  [...compatibleIdentities, ...identities.filter((i) => !identityFitsPlacement(i.identityType, tiktokPlacement))].map((i) => (
                    <option key={i.identityId} value={i.identityId} disabled={!identityFitsPlacement(i.identityType, tiktokPlacement)}>
                      {i.displayName ?? i.identityId} ({i.identityType})
                      {identityFitsPlacement(i.identityType, tiktokPlacement) ? '' : tiktokPlacement ? ' — not allowed on TikTok in-feed' : ' — not allowed on Pangle'}
                    </option>
                  ))
                )}
              </select>
              <Button variant="secondary" type="button" title="Refresh identities" onClick={() => reloadIdentities()} loading={identitiesLoading} disabled={identitiesLoading}>
                <RefreshCw size={14} />
              </Button>
              <Button variant="secondary" type="button" onClick={() => setIdentityModalOpen((v) => !v)} disabled={locked} title="Create a custom identity (Pangle / Global App Bundle)">
                <UserPlus size={13} /> New
              </Button>
            </div>
            {!selectedIdentityFits && (
              <p className="mt-1 text-2xs text-warning">
                {tiktokPlacement
                  ? 'This identity is a custom identity — TikTok rejects those for in-feed ads. Pick an authorised TikTok account.'
                  : 'This identity is a TikTok account — Pangle / Global App Bundle need a custom identity instead.'}
              </p>
            )}
            {identities.length === 0 && !identitiesLoading && (
              <p className="mt-1 text-2xs text-text-muted">
                No identities on this advertiser. Link a TikTok account in Ads Manager (ad creation → "Connect account") or Business Center → Assets → TikTok accounts, then refresh.
              </p>
            )}
          </FormField>

          {identityModalOpen && (
            <div className="-mt-2 mb-4 space-y-2 rounded-xl border border-border bg-surface-alt/40 p-3">
              <p className="text-xs text-text-muted">
                Creates a CUSTOMIZED_USER identity — display-name only, no real TikTok account. Only accepted on Pangle / Global App Bundle placements.
              </p>
              <div className="flex gap-2">
                <input className={clsx(inputCls, 'flex-1')} placeholder="Display name (e.g. Acme Stores)" value={newIdentityName} onChange={(e) => setNewIdentityName(e.target.value)} autoFocus />
                <Button variant="primary" onClick={createIdentity} loading={creatingIdentity} disabled={!newIdentityName.trim()}>Create</Button>
                <Button variant="ghost" onClick={() => setIdentityModalOpen(false)} disabled={creatingIdentity}>Cancel</Button>
              </div>
            </div>
          )}
        </div>

        {needsLeadForm && (
          <div className="sm:col-span-2">
            <FormField
              label="Instant Form"
              required
              hintBelow
              hint="Lead-generation ads open this form instead of a website. Forms are created in TikTok Ads Manager → Assets → Instant Forms."
            >
              <div className="flex gap-2">
                <select
                  className={clsx(inputCls, 'flex-1')}
                  value={pageId}
                  onFocus={() => { if (leadForms === null) void reloadLeadForms(); }}
                  onChange={(e) => set('pageId', e.target.value)}
                  disabled={locked}
                >
                  <option value="">{leadFormsLoading ? 'Loading forms…' : '— Select a form —'}</option>
                  {(leadForms ?? []).map((fm) => <option key={fm.pageId} value={fm.pageId}>{fm.title}</option>)}
                </select>
                <Button variant="secondary" type="button" onClick={() => reloadLeadForms()} loading={leadFormsLoading} title="Re-check TikTok for new forms">
                  <RefreshCw size={14} />
                </Button>
                <a
                  href={tiktokInstantFormsUrl(tiktokAccount?.advertiserId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-xl border border-border bg-surface px-3 py-2 text-sm font-medium text-text-primary hover:border-border-strong hover:bg-surface-alt"
                >
                  <ExternalLink size={13} /> Create form on TikTok
                </a>
              </div>
              {leadForms !== null && leadForms.length === 0 && !leadFormsLoading && (
                <p className="mt-1 text-2xs text-warning">
                  No Instant Forms on this advertiser yet. TikTok doesn't allow forms to be created over its API — use "Create form on TikTok", publish it there, then refresh.
                </p>
              )}
            </FormField>
          </div>
        )}

        {/* ── Creative: upload or reuse ── */}
        <div className="sm:col-span-2">
          <FormField
            label={isVideo ? 'Ad video' : adFormat === 'CAROUSEL_ADS' ? 'Carousel images (2–10)' : 'Ad image'}
            required
            hint={isVideo ? '9:16 · 1:1 · 16:9 — min 540×960 / 960×540, ≥516 kbps, 5–600 s, up to 500 MB' : 'JPEG / PNG'}
          >
            {!locked && (
              <div className="mb-2 inline-flex gap-0.5 rounded-lg border border-border bg-surface-alt p-0.5">
                {(['upload', 'existing'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setMediaTab(t)}
                    className={clsx(
                      'rounded-md px-3 py-1 text-xs font-medium transition-all',
                      mediaTab === t ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted hover:text-text-primary',
                    )}
                  >
                    {t === 'upload' ? 'Upload' : 'Use existing'}
                  </button>
                ))}
              </div>
            )}

            {!locked && mediaTab === 'upload' && (
              <div className="space-y-2">
                <label className={clsx(
                  'flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-xs text-text-muted hover:bg-surface-alt/50',
                  uploading && 'pointer-events-none opacity-60',
                )}>
                  {uploading ? <Loader2 size={14} className="shrink-0 animate-spin" />
                    : hasMedia ? <CheckCircle2 size={14} className="shrink-0 text-success" />
                    : <Upload size={14} className="shrink-0" />}
                  <span className="truncate">{uploading ? 'Uploading to TikTok…' : mediaNote ?? `Choose ${isVideo ? 'a video' : 'an image'}…`}</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    disabled={uploading}
                    accept={isVideo ? 'video/mp4,video/quicktime,video/webm' : 'image/jpeg,image/png'}
                    onChange={(e) => { const file = e.target.files?.[0]; if (file) void uploadFile(file); }}
                  />
                </label>
                <div className="flex gap-2">
                  <input
                    className={clsx(inputCls, 'flex-1 !py-1.5 text-xs')}
                    type="url"
                    placeholder={isVideo ? 'https://…/video.mp4 (public URL — many CDNs are refused by TikTok)' : 'https://…/image.jpg'}
                    value={creativeUrl}
                    onChange={(e) => set('creativeUrl', e.target.value)}
                  />
                  <Button variant="secondary" size="sm" onClick={uploadCreative} loading={uploading} disabled={!creativeUrl.trim()} iconLeft={<FileUp size={12} />}>
                    From URL
                  </Button>
                </div>
              </div>
            )}

            {!locked && mediaTab === 'existing' && (
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="flex w-full items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-left text-xs text-text-muted hover:bg-surface-alt/50"
              >
                {hasMedia ? <CheckCircle2 size={14} className="shrink-0 text-success" /> : <FolderOpen size={14} className="shrink-0" />}
                <span className="truncate">{mediaNote ?? `Browse ${isVideo ? 'videos' : 'images'} already on this advertiser…`}</span>
              </button>
            )}

            {locked && <div className="text-xs text-text-secondary">{mediaNote ?? '—'}</div>}

            {hasMedia && (
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-success-soft px-2 py-0.5 font-mono text-2xs text-success">
                  {isVideo ? `video_id ${resolvedVideoId}` : adFormat === 'CAROUSEL_ADS' ? `${carouselImageIds.length} image${carouselImageIds.length === 1 ? '' : 's'}` : `image_id ${resolvedImageId}`}
                  {!locked && (
                    <button type="button" onClick={clearMedia} title="Remove" className="ml-0.5 rounded-full p-0.5 text-success/70 transition-colors hover:bg-danger-soft hover:text-danger">
                      <X size={11} />
                    </button>
                  )}
                </span>
                {isVideo && videoGate.state === 'processing' && !locked && (
                  <Pill tone="warn"><RefreshCw size={9} className="animate-spin" /> Processing on TikTok…</Pill>
                )}
                {isVideo && videoGate.state === 'ready' && !locked && (
                  <Pill tone="ok">Ready{videoGate.width ? ` · ${videoGate.width}×${videoGate.height}` : ''}</Pill>
                )}
              </div>
            )}
            {isVideo && videoGate.state === 'rejected' && !locked && (
              <div className="mt-1.5 rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-2xs text-danger">
                This video can&apos;t be used: {videoGate.reason}
              </div>
            )}

            {adFormat === 'CAROUSEL_ADS' && carouselImageIds.length > 0 && (
              <ul className="mt-2 space-y-1">
                {carouselImageIds.map((id, i) => (
                  <li key={`${id}-${i}`} className="flex items-center gap-2 rounded-lg border border-border bg-surface px-2.5 py-1">
                    <span className="flex h-5 w-5 items-center justify-center rounded-md bg-primary-soft text-2xs font-bold text-primary">{i + 1}</span>
                    <span className="flex-1 truncate font-mono text-2xs text-text-secondary">{id}</span>
                    {!locked && (
                      <button type="button" onClick={() => set('carouselImageIds', carouselImageIds.filter((_, j) => j !== i))} className="text-text-muted hover:text-danger">
                        <X size={12} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </FormField>
        </div>

        <div className="sm:col-span-2">
          <FormField label={`Ad text (${adText.length}/${AD_TEXT_MAX})`} required>
            <textarea
              rows={2}
              maxLength={AD_TEXT_MAX}
              className={clsx(inputCls, 'resize-none')}
              aria-label="Ad text" placeholder="The caption shown on the ad"
              value={adText}
              onChange={(e) => set('adText', e.target.value)}
              disabled={locked}
            />
          </FormField>
        </div>

        {showCta ? (
          <FormField label="Call to action">
            <select aria-label="Call to action" className={inputCls} value={callToAction} onChange={(e) => set('callToAction', e.target.value)} disabled={locked}>
              {CTA_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </FormField>
        ) : (
          <div className="mb-4 rounded-xl border border-info/30 bg-info-soft px-3 py-2 text-2xs text-info">
            Followers ads drive to your TikTok profile — TikTok rejects any call to action or link on them, so none is sent.
          </div>
        )}

        {showLanding ? (
          <FormField required label="Landing page URL" hintBelow hint="Where the CTA sends the user.">
            <input className={inputCls} aria-label="Landing page URL" type="url" placeholder="https://your-site.com/landing" value={landingPageUrl} onChange={(e) => set('landingPageUrl', e.target.value)} disabled={locked} />
          </FormField>
        ) : destination === 'app' ? (
          <div className="mb-4 rounded-xl border border-info/30 bg-info-soft px-3 py-2 text-2xs text-info">
            App ads send people to the store listing of the ad group's app — no landing page is needed.
          </div>
        ) : null}
      </div>

      <ExistingCreativePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        kind={isVideo ? 'video' : 'image'}
        onPickVideo={pickVideo}
        onPickImage={pickImage}
        onPickFromAd={pickFromAd}
      />
    </div>
  );
};
